import { useState } from "react";
import { getIconByName } from "@/lib/icon-registry";
import { ImageOff } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type IconOrImageValue =
  | { type: "icon"; name: string }
  | { type: "image"; url: string }
  | string  // legacy: image URL or icon name fallback
  | null
  | undefined;

interface EntityIconProps {
  /** Icon name from registry, or image URL, or structured value. */
  value?: IconOrImageValue;
  /** Lucide component override (when known statically, e.g. from slug map) */
  fallbackIcon?: LucideIcon;
  className?: string;
  imgClassName?: string;
  alt?: string;
  strokeWidth?: number;
  /** Auto-invert dark PNG/SVG into white for dark backgrounds (hero overlay). */
  invertOnDark?: boolean;
}

/**
 * Renders either a Lucide icon (from the registry) or an image,
 * giving content editors a unified way to assign visuals to entities.
 * Falls back gracefully if the image fails to load (e.g. .ico files).
 */
const EntityIcon = ({
  value,
  fallbackIcon: Fallback,
  className = "h-12 w-12 text-foreground/70",
  imgClassName,
  alt = "",
  strokeWidth = 1.5,
  invertOnDark = false,
}: EntityIconProps) => {
  const [imgError, setImgError] = useState(false);

  const invertClass = invertOnDark ? "[filter:brightness(0)_invert(1)]" : "";
  const iconColorOverride = invertOnDark ? "text-white" : "";

  const mergeClass = (base: string, extra: string) =>
    extra ? `${base} ${extra}` : base;

  const renderImageFallback = () => {
    const cls = mergeClass(className, iconColorOverride);
    if (Fallback) return <Fallback className={cls} strokeWidth={strokeWidth} />;
    return <ImageOff className={cls} strokeWidth={strokeWidth} />;
  };

  // Structured value
  if (value && typeof value === "object") {
    if (value.type === "icon") {
      const Icon = getIconByName(value.name) || Fallback;
      if (Icon) return <Icon className={mergeClass(className, iconColorOverride)} strokeWidth={strokeWidth} />;
    }
    if (value.type === "image" && value.url) {
      if (imgError) return renderImageFallback();
      return (
        <img
          src={value.url}
          alt={alt}
          className={mergeClass(imgClassName || className, invertClass)}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      );
    }
  }

  // Legacy string: try icon name first, otherwise treat as URL
  if (typeof value === "string" && value.length > 0) {
    const Icon = getIconByName(value);
    if (Icon) return <Icon className={mergeClass(className, iconColorOverride)} strokeWidth={strokeWidth} />;
    if (value.startsWith("http") || value.startsWith("/")) {
      if (imgError) return renderImageFallback();
      return (
        <img
          src={value}
          alt={alt}
          className={mergeClass(imgClassName || className, invertClass)}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      );
    }
  }

  if (Fallback) return <Fallback className={mergeClass(className, iconColorOverride)} strokeWidth={strokeWidth} />;
  return null;
};

export default EntityIcon;
